<template>
  <div v-if="isVisible && currentStep" class="tutorial-overlay" :style="dialogStyle">
    <UiCard class="tutorial-overlay__card">
      <div class="tutorial-overlay__header">
        <div>
          <h3 class="tutorial-overlay__title">{{ currentStep.title }}</h3>
          <div class="tutorial-overlay__meta">
            Шаг {{ currentStepIndex + 1 }} из {{ currentTutorial?.steps.length }}
            <span v-if="currentTutorial?.estimatedTime"
              >(~{{ currentTutorial.estimatedTime }} мин)</span
            >
          </div>
        </div>
        <UiButton variant="secondary" @click="skipTutorial"> Закрыть </UiButton>
      </div>

      <UiProgress variant="linear" :value="progress" :indeterminate="false" />

      <div class="tutorial-overlay__body">
        <p>{{ currentStep.content }}</p>
        <UiAlert v-if="currentStep.action" type="info">
          {{ getActionText(currentStep.action) }}
        </UiAlert>
      </div>

      <div class="tutorial-overlay__actions">
        <UiButton v-if="!isFirstStep" variant="secondary" @click="prevStep">Назад</UiButton>
        <div class="tutorial-overlay__spacer" />
        <UiButton v-if="!isLastStep" variant="secondary" @click="skipTutorial">
          Пропустить
        </UiButton>
        <UiButton @click="nextStep">{{ isLastStep ? 'Завершить' : 'Далее' }}</UiButton>
      </div>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { computed, type CSSProperties } from 'vue'
import { useTutorial } from '@/modules/tutorial/composables/useTutorial'
import UiAlert from '@/shared/ui/UiAlert.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiProgress from '@/shared/ui/UiProgress.vue'

const {
  currentTutorial,
  currentStep,
  currentStepIndex,
  isVisible,
  isFirstStep,
  isLastStep,
  progress,
  nextStep,
  prevStep,
  skipTutorial,
} = useTutorial()

const dialogStyle = computed(() => {
  const centeredStyle: CSSProperties = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 10001,
  }

  if (!currentStep.value?.target) {
    return centeredStyle
  }

  const element = document.querySelector(currentStep.value.target)
  if (!element) {
    return centeredStyle
  }

  const rect = element.getBoundingClientRect()
  const position = currentStep.value.position || 'bottom'

  let top = '50%'
  let left = '50%'
  let transform = 'translate(-50%, -50%)'

  switch (position) {
    case 'top':
      top = `${rect.top - 12}px`
      left = `${rect.left + rect.width / 2}px`
      transform = 'translate(-50%, -100%)'
      break
    case 'bottom':
      top = `${rect.bottom + 12}px`
      left = `${rect.left + rect.width / 2}px`
      transform = 'translate(-50%, 0)'
      break
    case 'left':
      top = `${rect.top + rect.height / 2}px`
      left = `${rect.left - 12}px`
      transform = 'translate(-100%, -50%)'
      break
    case 'right':
      top = `${rect.top + rect.height / 2}px`
      left = `${rect.right + 12}px`
      transform = 'translate(0, -50%)'
      break
  }

  return {
    position: 'fixed',
    top,
    left,
    transform,
    zIndex: 10001,
  } as CSSProperties
})

const getActionText = (action: string) => {
  switch (action) {
    case 'click':
      return 'Нажмите на выделенный элемент'
    case 'hover':
      return 'Наведите курсор на выделенный элемент'
    case 'input':
      return 'Введите текст в поле ввода'
    case 'wait':
      return 'Подождите немного...'
    default:
      return 'Выполните действие'
  }
}
</script>

<style scoped>
.tutorial-overlay {
  pointer-events: none;
}

.tutorial-overlay__card {
  width: min(500px, calc(100vw - 24px));
  pointer-events: all;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.tutorial-overlay__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.tutorial-overlay__title {
  margin: 0;
  font-size: 18px;
}

.tutorial-overlay__meta {
  margin-top: 4px;
  color: #586578;
  font-size: 12px;
}

.tutorial-overlay__body {
  margin: 12px 0;
  display: grid;
  gap: 12px;
}

.tutorial-overlay__body p {
  margin: 0;
  line-height: 1.6;
}

.tutorial-overlay__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tutorial-overlay__spacer {
  flex: 1;
}

@media (max-width: 600px) {
  .tutorial-overlay {
    top: auto !important;
    left: 12px !important;
    right: 12px !important;
    bottom: 12px;
    transform: none !important;
  }

  .tutorial-overlay__card {
    width: auto;
  }
}
</style>
